import { Description, Tune } from '@mui/icons-material';
import {
  Box,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { deleteWriteOff } from 'lib/api/writeOffs';
import { WriteOff } from 'lib/api/writeOffs/types';
import CustomButton from 'lib/designSystem/Button';
import TableCard from 'lib/designSystem/Card/TableCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SmallHeaderWithIcon from 'lib/designSystem/SmallHeaderWithIcon';
import {
  formatDate,
  formatMoney,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import { SummaryRow } from 'pages/Finance/Components/Financing/common/Summary';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

interface WriteOffDetailsProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  writeOff: WriteOff;
}

const WriteOffDetails: React.FC<WriteOffDetailsProps> = ({
  open,
  setOpen,
  writeOff,
}) => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const canDelete = validatePermissions(user, 'financing_write_off_delete');
  const dispatch = useDispatch();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const { mutate: deleteWriteOffMutation, isPending: isDeletingWriteOff } =
    deleteWriteOff(dispatch);

  const handleDelete = () => {
    deleteWriteOffMutation(writeOff.id, {
      onSuccess: () => {
        showAlert(dispatch, 'success', 'Castigo eliminado exitosamente');
        setOpen(false);
      },
      onError: () => {
        showAlert(
          dispatch,
          'error',
          'Ha habido un error al eliminar el castigo',
        );
      },
    });
  };

  return (
    <div>
      <Dialog open={open} maxWidth="lg" fullWidth>
        <DialogTitle>Detalles del castigo</DialogTitle>
        <DialogContent>
          {isDeletingWriteOff ? (
            <CenteredCircularProgress type="dialog" />
          ) : (
            <Box>
              <TableCard
                size="small"
                enabledEffects={false}
                sx={{
                  boxShadow: 'none',
                  background: 'transparent',
                  border: 'none',
                }}
              >
                <SmallHeaderWithIcon
                  icon={<Description fontSize="small" />}
                  title="Datos del documento"
                />
                <Grid container spacing={0} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ pr: 2 }}>
                      <SummaryRow
                        label="Folio"
                        value={writeOff.folio}
                        labelVariant="body2"
                        valueVariant="body2"
                      />
                      <SummaryRow
                        label="Emisor"
                        value={writeOff.businessName}
                        labelVariant="body2"
                        valueVariant="body2"
                        valueMaxLength={40}
                      />
                      <SummaryRow
                        label="Rut emisor"
                        value={writeOff.businessIdentifier}
                        labelVariant="body2"
                        valueVariant="body2"
                        showBorder={false}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        pl: 2,
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <SummaryRow
                        label="Monto documento"
                        value={formatMoney(writeOff.documentAmount)}
                        labelVariant="body2"
                        valueVariant="body2"
                      />
                      <SummaryRow
                        label="Deudor"
                        value={writeOff.stakeholderName}
                        labelVariant="body2"
                        valueVariant="body2"
                        valueMaxLength={40}
                      />
                      <SummaryRow
                        label="Rut deudor"
                        value={writeOff.stakeholderIdentifier}
                        labelVariant="body2"
                        valueVariant="body2"
                      />
                    </Box>
                  </Grid>
                </Grid>

                <SmallHeaderWithIcon
                  icon={<Tune fontSize="small" />}
                  title="Parámetros"
                />
                <Grid container spacing={0} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ pr: 2 }}>
                      <SummaryRow
                        label="Monto castigo"
                        value={formatMoney(writeOff.amount)}
                        labelVariant="body2"
                        valueVariant="body2"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        pl: 2,
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <SummaryRow
                        label="Fecha castigo"
                        value={formatDate(new Date(writeOff.writeOffAt))}
                        labelVariant="body2"
                        valueVariant="body2"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </TableCard>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 10,
              width: '100%',
              marginLeft: 10,
              marginRight: 10,
            }}
          >
            {canDelete ? (
              <Typography
                color="error"
                variant="body1"
                fontWeight="bold"
                style={{ cursor: 'pointer' }}
                onClick={() => setOpenDeleteDialog(true)}
              >
                Eliminar
              </Typography>
            ) : (
              <div />
            )}
            <CustomButton
              color="secondary"
              onClick={() => setOpen(false)}
              disabled={isDeletingWriteOff}
            >
              Cerrar
            </CustomButton>
          </div>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Eliminar castigo</DialogTitle>
        <DialogContent>
          {isDeletingWriteOff ? (
            <div style={{ minHeight: 100 }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          ) : (
            <Typography>
              ¿Estás seguro de que deseas eliminar este castigo?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setOpenDeleteDialog(false)}
          >
            Cancelar
          </CustomButton>
          <CustomButton color="error" onClick={handleDelete}>
            Eliminar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WriteOffDetails;
